/**
 * Shipping Bar Component
 *
 * Displays shipping countdown and free shipping progress.
 * Updates countdown every minute and responds to cart changes.
 */

(function() {
  'use strict';

  const selectors = {
    shippingBar: '[data-shipping-bar]',
    shippingText: '[data-shipping-text]',
    freeShippingText: '[data-free-shipping-text]',
    flag: '[data-shipping-flag]'
  };

  const dayNames = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  const dayLabels = {
    sun: 'Sunday',
    mon: 'Monday',
    tue: 'Tuesday',
    wed: 'Wednesday',
    thu: 'Thursday',
    fri: 'Friday',
    sat: 'Saturday'
  };

  let countdownInterval = null;

  /**
   * Parse shipping days from comma-separated string
   */
  function parseShippingDays(daysString) {
    if (!daysString) return [1, 2, 3, 4, 5]; // Default: Mon-Fri
    return daysString.toLowerCase().split(',')
      .map(day => day.trim())
      .map(day => dayNames.indexOf(day))
      .filter(index => index !== -1);
  }

  /**
   * Get next shipping day from today
   */
  function getNextShippingDay(shippingDays, fromDate, skipToday) {
    const date = new Date(fromDate);

    if (skipToday) {
      date.setDate(date.getDate() + 1);
    }

    // Look up to 7 days ahead
    for (let i = 0; i < 7; i++) {
      const dayOfWeek = date.getDay();
      if (shippingDays.includes(dayOfWeek)) {
        return { date, dayIndex: dayOfWeek };
      }
      date.setDate(date.getDate() + 1);
    }

    // Fallback (shouldn't happen if shippingDays has at least one day)
    return { date, dayIndex: date.getDay() };
  }

  /**
   * Get relative day label
   */
  function getRelativeDayLabel(targetDate, now) {
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);

    const target = new Date(targetDate);
    target.setHours(0, 0, 0, 0);

    const diffDays = Math.round((target - today) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'today';
    if (diffDays === 1) return 'tomorrow';
    return dayLabels[dayNames[target.getDay()]];
  }

  /**
   * Format countdown time
   */
  function formatCountdown(milliseconds) {
    const totalMinutes = Math.floor(milliseconds / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours > 0) {
      return hours + 'h ' + minutes + 'm';
    }
    return minutes + 'm';
  }

  /**
   * Calculate shipping countdown message
   */
  function getShippingMessage(dataset) {
    const cutoffHour = parseInt(dataset.cutoffHour, 10) || 14;
    const cutoffMinute = parseInt(dataset.cutoffMinute, 10) || 0;
    const shippingDaysStr = dataset.shippingDays || 'mon,tue,wed,thu,fri';
    const shippingDays = parseShippingDays(shippingDaysStr);

    const now = new Date();
    const todayDayOfWeek = now.getDay();
    const isShippingDay = shippingDays.includes(todayDayOfWeek);

    // Calculate cutoff time for today
    const cutoffToday = new Date(now);
    cutoffToday.setHours(cutoffHour, cutoffMinute, 0, 0);

    const isBeforeCutoff = now < cutoffToday;

    if (isShippingDay && isBeforeCutoff) {
      // Before cutoff on a shipping day - show countdown
      const timeRemaining = cutoffToday - now;
      const countdown = formatCountdown(timeRemaining);
      return {
        type: 'countdown',
        text: 'Ships',
        day: 'today',
        countdown: countdown
      };
    } else {
      // After cutoff or not a shipping day - show next shipping day
      // Skip today only if it's a shipping day (we missed cutoff), otherwise check from today
      const skipToday = isShippingDay;
      const nextShip = getNextShippingDay(shippingDays, now, skipToday);
      const label = getRelativeDayLabel(nextShip.date, now);

      return {
        type: 'next_day',
        text: 'Ships',
        day: label
      };
    }
  }

  /**
   * Format money amount
   */
  function formatMoney(cents, currencySymbol) {
    const amount = Math.ceil(cents / 100);
    return currencySymbol + amount;
  }

  /**
   * Get free shipping message
   */
  function getFreeShippingMessage(cartTotal, threshold, currencySymbol) {
    const thresholdCents = threshold * 100;

    if (cartTotal >= thresholdCents) {
      return {
        reached: true,
        html: '<strong>Free shipping</strong>'
      };
    }

    const remaining = thresholdCents - cartTotal;
    return {
      reached: false,
      html: formatMoney(remaining, currencySymbol) + ' away from <strong>free shipping</strong>'
    };
  }

  /**
   * Update a single shipping bar
   */
  function updateShippingBar(node, cartTotal) {
    if (!node) return;

    const dataset = node.dataset;
    const total = cartTotal !== undefined ? cartTotal : parseInt(dataset.cartTotal, 10) || 0;
    const threshold = parseFloat(dataset.freeShippingThreshold) || 60;
    const currencySymbol = dataset.currencySymbol || '€';

    // Update shipping countdown
    const shippingTextEl = node.querySelector(selectors.shippingText);
    if (shippingTextEl) {
      const shippingMsg = getShippingMessage(dataset);

      if (shippingMsg.type === 'countdown') {
        // "Ships today ⏱ 2h 34m" - day bold, countdown not bold
        shippingTextEl.innerHTML = shippingMsg.text + ' <span class="shipping-bar__day">' + shippingMsg.day + '</span> <span class="shipping-bar__countdown">\u23F1 ' + shippingMsg.countdown + '</span>';
      } else {
        // "Ships tomorrow" or "Ships Monday" - day bold
        shippingTextEl.innerHTML = shippingMsg.text + ' <span class="shipping-bar__day">' + shippingMsg.day + '</span>';
      }
    }

    // Update free shipping progress
    const freeShippingTextEl = node.querySelector(selectors.freeShippingText);
    if (freeShippingTextEl) {
      const freeShippingMsg = getFreeShippingMessage(total, threshold, currencySymbol);
      freeShippingTextEl.innerHTML = freeShippingMsg.html;

      // Add/remove success class
      if (freeShippingMsg.reached) {
        node.classList.add('shipping-bar--free-shipping');
      } else {
        node.classList.remove('shipping-bar--free-shipping');
      }
    }

    // Mark as loaded
    node.classList.add('shipping-bar--loaded');
  }

  /**
   * Update all shipping bars
   */
  function updateAllBars(cartTotal) {
    const bars = document.querySelectorAll(selectors.shippingBar);
    bars.forEach(function(bar) {
      updateShippingBar(bar, cartTotal);
    });
  }

  /**
   * Initialize all shipping bars
   */
  function init() {
    updateAllBars();

    // Start countdown interval (update every minute)
    if (countdownInterval) {
      clearInterval(countdownInterval);
    }
    countdownInterval = setInterval(function() {
      updateAllBars();
    }, 60000);
  }

  /**
   * Refresh from cart
   */
  function refresh() {
    fetch('/cart.js')
      .then(function(response) { return response.json(); })
      .then(function(cart) {
        updateAllBars(cart.total_price);
      })
      .catch(function(err) {
        console.error('Shipping bar: Error fetching cart:', err);
        updateAllBars();
      });
  }

  /**
   * Debounce helper
   */
  function debounce(func, wait) {
    let timeout;
    return function() {
      const args = arguments;
      const later = function() {
        clearTimeout(timeout);
        func.apply(null, args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  const debouncedRefresh = debounce(refresh, 100);

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Listen for cart updates
  document.addEventListener('cart:updated', debouncedRefresh);
  document.addEventListener('quick-cart:updated', debouncedRefresh);
  document.addEventListener('cart:change', debouncedRefresh);
  document.addEventListener('ajaxCart:updated', debouncedRefresh);

  // Expose for manual refresh
  window.shippingBar = { refresh: refresh, init: init };

})();
